import { GoToDialogHelper } from "csi/middleware/goToDialog/GoToDialogHelper";
import reflection from "csi/reflection";
import { C8Node, DUMMY, cnl } from "csi/typings";
import { formatNumber, formatNumberForEditing } from "csi/utility/numberUtility";
import xml from "csi/xml";
import { DndItemWithId } from "designSystem/dnd";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ObservableMap, action, computed, makeObservable, observable } from "mobx";
import React, { SyntheticEvent } from "react";
import { BaseCompositionStore, createBaseCompositionMaterialKey } from "./BaseCompositionStore";
import { CompositionPlacement } from "./CompositionPlacement";
import { SalesRegionException } from "./SalesRegionException";
import { isNullOrNaN, isNullOrUndefined } from "csi/utility/coreUtility";


export interface CompositionMaterialDetails {
	url: cnl;
	percentage: number;
	type: cnl;
}

export abstract class AbstractMaterial implements DndItemWithId {
	readonly baseCompositionStore: BaseCompositionStore;
	@observable rawPercentage: number;
	@observable private _isDirty: boolean;
	@observable protected _key: string;
	@observable protected _currentUrl: cnl;

	get id(): string {
		return this._key;
	}

	baseCompositionMaterialMap: ObservableMap<string, BaseCompositionMaterial> = observable.map<string, BaseCompositionMaterial>(null, { deep: false });

	compositionMaterialGoToDialogHelper: GoToDialogHelper;

	/** Computes an array of BaseCompositionMaterial objects from the baseCompositionMaterialMap. */
	@computed get baseCompositionMaterials(): BaseCompositionMaterial[] {
		return Array.from(this.baseCompositionMaterialMap.values());
	}

	/** loads an existing BaseCompositionMaterial instance to the baseCompositionMaterialMap. */
	@action loadBaseCompositionMaterial = (
		percent: number,
		url: cnl,
		sustainabilityTypeUrl: cnl,
		nestUnderContainsPlacement: boolean,
	): void => {
		const key = createBaseCompositionMaterialKey();
		this.baseCompositionMaterialMap.set(key, new BaseCompositionMaterial(this, key, percent, url, sustainabilityTypeUrl, nestUnderContainsPlacement));
		this.baseCompositionStore.focusDropDownBoolean = false;
	};

	/** Adds a new BaseCompositionMaterial instance to the baseCompositionMaterialMap. */
	@action addBaseCompositionMaterial = (): void => {
		const key = createBaseCompositionMaterialKey();
		this.baseCompositionMaterialMap.set(key, new BaseCompositionMaterial(this, key, null, null, null, false));
		this.baseCompositionStore.focusDropDownBoolean = true;
	};

	/** Removes a BaseCompositionMaterial instance from the baseCompositionMaterialMap. */
	@action removeBaseCompositionMaterial = (key: string): void => {
		this.baseCompositionMaterialMap.delete(key);
	};

	/** Retrieves composition types that pertain to the given composition material */
	getCompositionMaterialTypesForCompositionMaterial = (url: string): C8Node[] => {
		return this.baseCompositionStore.sustainabilityCompositionNodes.filter((n) => n.SustainabilityCompositionDependencies.includes(url));
	};

	/** Returns true if there is at least one composition type that pertains to the given composition material */
	hasCompositionMaterialTypesForDropdown = (url: string): boolean => {
		const nodes = this.getCompositionMaterialTypesForCompositionMaterial(url);
		return (nodes.length > 0);
	};

	/** This method checks if there are any placeholder (dummy) materials within the base composition materials. */
	get compositionMaterialsHaveDummy(): boolean {
		return this.baseCompositionMaterials.some((material) => material.currentUrl === DUMMY);
	}

	/** Retrieves an array of all available ComponentItems for placement, including the DUMMY item. */
	get allCompositionItemsAvailable(): ComponentItem[] {
		return [
			...this.baseCompositionStore.compositionNodes
				.map((node) => ({
					id: xml.nodeGetURL(node),
					invalid: !node.OKforStyle,
					node,
				}))];
	}

	/** Retrieves an array of all available ComponentItems for material, including the DUMMY item. */
	get materialsAsComponentItems(): ComponentItem[] {
		return [
			...this.baseCompositionStore.materialNodes
				.map((node) => ({
					id: xml.nodeGetURL(node),
					invalid: !node.Active,
					node,
				}))];
	}

	/** Retrieves the percentage value, ensuring it is within the range of 0 to 100. */
	get percentage(): number {
		if (this.rawPercentage < 0) {
			return 0;
		}
		return this.rawPercentage;
	}

	/** Retrieves percentage value, returning null if it is not within the range of 0 to 100. this is because we only want to modify the users input if they are out of bounds*/
	get restrictedPercentage(): number | null {
		const { rawPercentage } = this;
		if (!rawPercentage || rawPercentage >= 0) {
			return null;
		}
		return 0;
	}

	get key(): string {
		return this._key;
	}

	get currentUrl(): cnl {
		return this._currentUrl;
	}

	/** flag (user has previously made a change to percentage) */
	get isDirty(): boolean {
		return this._isDirty;
	}

	/** get percentage fixed to the length of the attribute */
	get percentageFixed(): string {
		const percentageAttribute = reflection.getTypeAttribute("CareAndCompositionLineItem", "PercentOfTotal");
		if (isNullOrNaN(this.rawPercentage)) {
			return "";
		}
		return formatNumberForEditing(this.percentage / 100, percentageAttribute.getFormat());
	}

	/** Common method to set percentage */
	@action setPercentage = (percentage: number): void => {
		this.rawPercentage = percentage;
	};

	/** Common method to set key */
	@action setKey = (key: string): void => {
		this._key = key;
	};

	/** Common method to set current URL */
	@action setCurrentUrl = (url: cnl): void => {
		this._currentUrl = url;
	};

	/** sets the url of the compositionMaterial */
	@action setUrlFromItem = (event: React.ChangeEvent<{}>, item: ComponentItem): void => {
		this._currentUrl = item.id;
		this.endSetUrlProcess();
	};

	/** Retrieves the current CompositionPlacement instance associated with a given key. */
	getCurrentCompositionMaterial = (compositionMaterialKey: string): BaseCompositionMaterial => {
		return this.baseCompositionMaterials.find((composition) => {
			return composition.key === compositionMaterialKey;
		});
	};

	/** empty load for overriding */
	endSetUrlProcess = async (): Promise<void> => { };

	/** set percentage */
	@action setMaterialPercentage = (event: React.ChangeEvent<HTMLInputElement>): void => {
		this.rawPercentage = parseFloat(event.target.value);
		this._isDirty = true;
	};

	/** goToDialog onClick that opens a list of the currently selectable CompositionMaterials */
	onClickCompositionMaterialGoToDialog = (e: SyntheticEvent): void => {
		const { baseCompositionStore, baseCompositionMaterials, allCompositionItemsAvailable, compositionMaterialGoToDialogHelper } = this;
		const compositionMaterialURLs = baseCompositionMaterials.length > 0 ? baseCompositionMaterials.map((cm) => cm.currentUrl) : [];
		const compositionMaterialSelections = baseCompositionStore.compositionNodes.filter((node) => compositionMaterialURLs.includes(node.$URL));
		const compositionMaterialCNLs = allCompositionItemsAvailable.filter((item) => (!item.invalid)).map((placementItem) => placementItem.id);
		compositionMaterialGoToDialogHelper.openGoToDialogWithCNLs(compositionMaterialCNLs, compositionMaterialSelections);
	};

	constructor(baseCompositionStore: BaseCompositionStore, key: string, percentage?: number, currentUrl?: cnl) {
		makeObservable(this);
		this.baseCompositionStore = baseCompositionStore;
		this._key = key;
		this.rawPercentage = percentage;
		this.setCurrentUrl(currentUrl ?? DUMMY);
		this.compositionMaterialGoToDialogHelper = new GoToDialogHelper(
			"Composition",
			true,
			(selectedUrls: cnl[]) => {
				const existingCompositions = this.baseCompositionMaterials.map((cm) => cm.currentUrl);

				// Removing unselected compositions
				this.baseCompositionMaterials
					.filter((cm) => !selectedUrls.includes(cm.currentUrl))
					.forEach((cm) => this.removeBaseCompositionMaterial(cm.key));

				// Adding new composition
				selectedUrls
					.filter((url) => !existingCompositions.includes(url))
					.forEach((url) => this.loadBaseCompositionMaterial(null, url, null, false));
			},
		);
	}
}

export class BaseCompositionMaterial extends AbstractMaterial {
	@observable currentType: cnl;
	@observable private _nestUnderContainsPlacement: boolean = false;
	readonly parent: AbstractMaterial | CompositionPlacement;
	percentageAttribute = reflection.getTypeAttribute("MaterialComposition", "Percentage");

	get currentMaterialNode(): C8Node | undefined {
		return this.baseCompositionStore.compositionNodes.find((composition) => composition.$URL === this.currentUrl);
	}

	get currentMaterialName(): string {
		return this.baseCompositionStore.loc.getNodeName(this.currentMaterialNode);
	}

	get currentMaterialTypeNode(): C8Node | undefined {
		return this.baseCompositionStore.sustainabilityCompositionNodes.find((node) => node.$URL === this.currentType);
	}

	get currentMaterialTypeName(): string {
		return this.baseCompositionStore.loc.getNodeName(this.currentMaterialTypeNode);
	}

	getRightDrawerMaterialText = (salesRegionException: SalesRegionException): string => {
		const { currentMaterialName, currentMaterialNode, currentMaterialTypeName, percentage, nestUnderContainsPlacement } = this;
		if (!currentMaterialNode) {
			return "";
		}

		let materialText = currentMaterialName;
		let percentText = "";
		const includeType = currentMaterialTypeName && salesRegionException.includeCompositionTypes;
		const typeText = includeType ? ` ${currentMaterialTypeName}` : "";

		if (!nestUnderContainsPlacement && !isNullOrUndefined(percentage) && !isNullOrNaN(this.rawPercentage)) {
			percentText = formatNumber(this.percentage / 100, this.percentageAttribute.getFormat());
		}

		const languageSyntax = xml.enumGetText(this.baseCompositionStore.languageSyntax);
		
		switch (languageSyntax) {
			case "PercentageTypeMaterial":
				materialText = `${percentText} ${typeText} ${materialText}`;
				break;
			case "PercentageMaterialType":
				materialText = `${percentText} ${materialText} ${typeText}`;
				break;
			case "TypeMaterialPercentage":
				materialText = `${typeText} ${materialText} ${percentText}`;
				break;
			case "MaterialTypePercentage":
				materialText = `${materialText} ${typeText} ${percentText}`;
				break;
			default:
				break;
		}

		return materialText;
	};

	/** Returns choices for material types dropdown */
	getCompositionMaterialTypesForDropdown = (url: string): ComponentItem[] => {
		const { baseCompositionStore, currentCompositionTypeDropdownValue, currentUrl, getCompositionMaterialTypesForCompositionMaterial, parent } = this;
		const nodes = getCompositionMaterialTypesForCompositionMaterial(url);
		const typesToRemove = parent.baseCompositionMaterials
			.filter((comp) => comp.currentType !== currentCompositionTypeDropdownValue?.id && currentUrl === comp.currentUrl)
			.map((comp) => comp.currentType);

		const filteredItems = nodes
			.filter((item) =>
				!typesToRemove.includes(item.$URL) &&
				(item.Active || currentCompositionTypeDropdownValue?.id === item.$URL))
			.map((item) => ({
				id: item.$URL,
				invalid: !item.Active,
				node: item,
			}));

		filteredItems.unshift(this.baseCompositionStore.dummyItemList as any);
		return filteredItems;
	};

	/** Retrieves an array of all available ComponentItems for placement type */
	get allCompositionTypeItemsAvailable(): ComponentItem[] {
		return this.baseCompositionStore.sustainabilityCompositionNodes
			.map((node) => ({
				id: xml.nodeGetURL(node),
				node,
			}));
	}

	/** get percentage fixed to the length of the attribute */
	get percentageFixed(): string {
		if (isNullOrNaN(this.rawPercentage)) {
			return "";
		}
		return formatNumberForEditing(this.percentage / 100, this.percentageAttribute.getFormat());
	}

	/** Retrieves an array of ComponentItems suitable for use in a dropdown menu, excluding invalid items. */
	get compositionMaterialItemsForDropdown(): ComponentItem[] {
		return this.allCompositionItemsAvailable.filter((item) => (!item.invalid) || this.currentUrl === item.id);
	};

	/** Gets the current dropdown value for a given CompositionPlacementType. */
	get currentCompositionTypeDropdownValue(): ComponentItem | null {
		if (this.currentType === DUMMY) {
			return null;
		}
		return this.allCompositionTypeItemsAvailable.find((item) => item.id === this.currentType) ?? null;
	};

	/** Gets the current dropdown value for a given CompositionPlacement. */
	get currentCompositionDropdownValue(): ComponentItem | null {
		if (this.currentUrl === DUMMY) {
			return null;
		}
		return this.allCompositionItemsAvailable.find((item) => item.id === this.currentUrl) ?? null;
	};

	/** return nestUnderContainsPlacement boolean value */
	get nestUnderContainsPlacement(): boolean {
		return this._nestUnderContainsPlacement;
	}

	getShowErrorCompositionMaterial = (percentageIsInvalid: boolean): boolean => {
		const { searchKeyInBannerConditions, enforceHundredPercent, fromStyleBom } = this.baseCompositionStore;
		let showError = (fromStyleBom || enforceHundredPercent) && percentageIsInvalid && !!this.percentage;
		showError ||= !this.percentage && searchKeyInBannerConditions("BannerSpecifyPercentagePerCompositionMaterial");
		return showError;
	};

	/** set percentage */
	@action setCompositionMaterialPercentage = (event: React.ChangeEvent<HTMLInputElement>): void => {
		this._nestUnderContainsPlacement = false;
		this.rawPercentage = parseFloat(event.target.value);
	};

	/** sets the url of the compositionMaterial type */
	@action setCompositionMaterialTypeUrl = (event: React.ChangeEvent<{}>, item: ComponentItem): void => {
		this.currentType = item.id;
	};

	/** toggle nestUnderContainsPlacement */
	@action setNestUnderContainsPlacement = (event: React.ChangeEvent<HTMLInputElement>, nestUnderContainsPlacement: boolean): void => {
		this._nestUnderContainsPlacement = nestUnderContainsPlacement;
	};

	override endSetUrlProcess = async (): Promise<void> => {
		this.currentType = DUMMY;
	};

	@computed get compositionMaterialDetails(): CompositionMaterialDetails {
		return {
			url: this.currentUrl,
			percentage: this.percentage,
			type: this.currentType,
		};
	}

	constructor(parent: CompositionPlacement | AbstractMaterial, key: string, percentage?: number, currentUrl?: cnl, currentType?: cnl, nestUnderContainsPlacement?: boolean) {
		super(parent.baseCompositionStore, key, percentage, currentUrl);
		this.parent = parent;
		this._nestUnderContainsPlacement = nestUnderContainsPlacement;
		makeObservable(this);
		this.currentType = currentType ?? DUMMY;
	}
}